import { ImageSize, objectToURLSearchParams } from '../..';
import { RecolourSearchParams } from '../../schemas';
import { withDomain } from '../domain';

const basePath = '/api';
const auth = (path: string) => `${basePath}/auth/${path}`;
const images = (path: string) => `${basePath}/images/${path}`;
const content = (path: string) => `${basePath}/content/${path}`;
const utils = (path: string) => `${basePath}/utils/${path}`;
const commerce = `${basePath}/commerce`;
const cart = `${commerce}/cart`;
const quote = `${commerce}/quote`;
const checkout = `${commerce}/checkout`;

export interface MockupParams {
	name: string;
	merchId: string;
	variantId?: string;
	side?: string;
}

export interface ProductImageParams {
	size?: ImageSize;
	svg?: boolean;
	productCode: string;
	colourCode?: string;
	side?: string;
	webDomain?: string;
	design?: {
		url: string;
		left: number;
		top: number;
		width: number;
		height: number;
	};
}

export interface MerchImageParams {
	merchId: string;
	colourCode?: string | null;
	side?: string;
	size?: ImageSize;
	webDomain?: string;
	updatedAt: string | Date;
}

export interface OrderImageParams {
	orderId: string;
	itemId?: string;
	side?: string;
	size?: ImageSize;
	webDomain?: string;
	updatedAt: string | Date;
}

export interface DesignImageParams {
	designId: string;
	updatedAt: string | Date;
	size?: ImageSize;
	width?: number;
	height?: number;
}

export default {
	API: {
		content: {
			journals: content('journals'),
			instagram: content('instagram'),
			topProducts: content('top-products'),
			faq: content('faq'),
			exampleDesigner: content('example-designer'),
		},
		auth: {
			fetchSession: auth('session'),
		},
		utils: {
			recolour: (search: RecolourSearchParams) => utils(`recolour?${objectToURLSearchParams(search).toString()}`),
		},
		bucket: (bucket: string, fileName: string) => `${basePath}/bucket/${bucket}/${fileName}`,
		images: {
			unsplash: images('unsplash'),
			productImage: ({ productCode, colourCode, side, webDomain, design, size, svg }: ProductImageParams) => {
				let url = images(`product/${productCode}${colourCode ? `/${colourCode}` : ''}${colourCode && side ? `/${side}` : ''}.png`);

				const searchParams = design ? objectToURLSearchParams(design) : new URLSearchParams();
				if (size) searchParams.append('size', size.toString());
				if (svg) searchParams.append('svg', 'true');
				const search = searchParams.toString();
				if (search) url += `?${search}`;

				return webDomain ? withDomain(url, webDomain) : url;
			},
			labelImage: ({ labelId, preview, png }: { labelId: string; preview?: boolean; png?: boolean }) =>
				images(`label/${labelId}/${preview ? `preview.${png ? 'png' : 'webp'}` : 'artwork.png'}`),
			// Canonical way to fetch a Design's rendered image. Handles lazy-cropped
			// designs (Design.cropS*) transparently — the endpoint sharp-extracts
			// on demand for those, 302s to the raw source otherwise. Prefer this
			// over `design.image.url` in any UI consumer so cropped designs render
			// as the user's chosen crop, not the raw source.
			designImage: ({
				designId,
				size,
				webDomain,
				updatedAt,
			}: {
				designId: string;
				size?: number;
				webDomain?: string;
				updatedAt?: string | Date;
			}) => {
				let url = images(`design/${designId}.png`);
				if (size) url += `?size=${size}`;
				if (updatedAt) url += `${size ? '&' : '?'}updatedAt=${new Date(updatedAt).getTime()}`;
				return webDomain ? withDomain(url, webDomain) : url;
			},
			printImage: ({
				printId,
				type,
				png,
				updatedAt,
			}: {
				printId: string;
				type: 'preview' | 'dst' | 'pdf' | 'artwork';
				png?: boolean;
				updatedAt?: string | Date;
			}) =>
				images(
					`print/${printId}/${
						type === 'dst'
							? 'artwork.dst'
							: type === 'pdf'
							? 'artwork.pdf'
							: type === 'preview'
							? `preview.${png ? 'png' : 'webp'}`
							: 'artwork.png'
					}${updatedAt ? `?updatedAt=${new Date(updatedAt).getTime()}` : ''}`,
				),
			merchImage: ({ merchId, colourCode, side, size, webDomain, updatedAt }: MerchImageParams) => {
				let url = images(`merch/${merchId}${side ? `/${side}` : ''}${side && colourCode ? `/${colourCode}` : ''}.png`);

				const searchParams = new URLSearchParams();
				if (size) searchParams.append('size', size.toString());
				searchParams.append('updatedAt', new Date(updatedAt).getTime().toString());
				const search = searchParams.toString();
				if (search) url += `?${search}`;

				return webDomain ? withDomain(url, webDomain) : url;
			},
			orderImage: ({ orderId, itemId, side, webDomain, size, updatedAt }: OrderImageParams) => {
				let url = images(`order-preview/${orderId}${itemId ? `/${itemId}` : ''}${side ? `/${side}` : ''}.png`);

				const searchParams = new URLSearchParams();
				if (size) searchParams.append('size', size.toString());
				searchParams.append('updatedAt', new Date(updatedAt).toISOString());
				const search = searchParams.toString();
				if (search) url += `?${search}`;
				return webDomain ? withDomain(url, webDomain) : url;
			},
			viewUpload: (fileName: string) => images(`view-upload/${fileName}`),
			getSignedUpload: images('signed-upload'),
		},
		commerce: {
			userDesigns: `${commerce}/user-designs`,
			design: {
				colourConvert: `${commerce}/design/colour-convert`,
				embroideryStitches: `${commerce}/design/embroidery-stitches`,
				embroideryTrueview: `${commerce}/design/embroidery-trueview`,
				embroideryPreview: `${commerce}/design/embroidery-preview`,
			},
			operationalSettings: {
				get: `${commerce}/operational-settings`,
				timeframes: `${commerce}/operational-settings/timeframes`,
			},
			products: {
				top: `${commerce}/products/top`,
				search: `${commerce}/products/search`,
				related: (slug: string, category: string) =>
					`${commerce}/products/related?slug=${encodeURIComponent(slug)}&category=${encodeURIComponent(category)}`,
				get: (productCode: string) => {
					const base = `${commerce}/products/${productCode}`;

					return {
						price: `${base}/price`,
						options: `${base}/options`,
					};
				},
			},
			cart: {
				get: `${cart}/get`,
				add: `${cart}/add`,
				remove: `${cart}/remove`,
				update: `${cart}/update`,
				type: `${cart}/type`,
				shipping: `${cart}/shipping`,
				coupon: {
					add: `${cart}/coupon/add`,
					remove: `${cart}/coupon/remove`,
				},
			},
			quote: {
				get: `${quote}/get`,
				add: `${quote}/add`,
				remove: `${quote}/remove`,
				update: `${quote}/update`,
				shipping: `${quote}/shipping`,
				coupon: {
					add: `${quote}/coupon/add`,
					remove: `${quote}/coupon/remove`,
				},
			},
			checkout: {
				express: {
					update: `${checkout}/express/update`,
					charge: `${checkout}/express/charge`,
				},
			},
			powerboard: {
				vault: `${commerce}/powerboard/vault`,
				auth: `${commerce}/powerboard/authorize-charge`,
			},
		},
	},
} as const;
